import {
  getDayDateKey,
  parseDateTimeString,
  setDateOnDateTime,
} from './itineraryDragUtils';
import { getDayIndexFromTripStart, toDateKey } from './roadTripDateUtils';

export const parseTransportationData = (item) => {
  const raw = item?.transportation_data;
  if (!raw) {
    return {};
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
};

export const isDraggableRoadTripCard = (item) => Boolean(
  item?._isRoadTripCard
  && item?._roadTripGroupId
  && (item._roadTripStops?.length || item._roadTripPois?.length)
);

export const getRoadTripSegmentItems = (roadTripCard) => [
  ...(roadTripCard?._roadTripStops || []),
  ...(roadTripCard?._roadTripPois || []),
];

const compareByStart = (a, b) => (
  String(a?.start_datetime || '').localeCompare(String(b?.start_datetime || ''))
  || (Number(a?.id) || 0) - (Number(b?.id) || 0)
);

/**
 * A road trip day in driving order: each main stop followed by the places
 * saved near it (items whose transportation_data.parentStop is that stop).
 *
 * @returns {Array<{ entry: object, isPlace: boolean }>}
 */
export const buildRoadTripDayRoute = (stopItems = [], placeItems = []) => {
  const remainingPlaces = [...(placeItems || [])].sort(compareByStart);
  const route = [];
  [...(stopItems || [])].sort(compareByStart).forEach((stop) => {
    route.push({ entry: stop, isPlace: false });
    for (let i = 0; i < remainingPlaces.length;) {
      if (parseTransportationData(remainingPlaces[i]).parentStop === stop.name) {
        route.push({ entry: remainingPlaces.splice(i, 1)[0], isPlace: true });
      } else {
        i += 1;
      }
    }
  });
  remainingPlaces.forEach((place) => route.push({ entry: place, isPlace: true }));
  return route;
};

export const getRoadTripDayCount = (roadTripData) => {
  const allDays = roadTripData?.allDaysData;
  if (Array.isArray(allDays) && allDays.length > 0) {
    return allDays.length;
  }
  return 1;
};

export const getRoadTripSegmentNumber = (roadTripData) => {
  const allDays = roadTripData?.allDaysData || [];
  const segmentDayIndex = roadTripData?.dayIndex ?? 0;

  if (!allDays.length) {
    return segmentDayIndex + 1;
  }

  const segmentIndex = allDays.findIndex((dayEntry, idx) => (
    (dayEntry.dayIndex ?? idx) === segmentDayIndex
  ));

  return segmentIndex >= 0 ? segmentIndex + 1 : segmentDayIndex + 1;
};

const routeLabel = (fromName, toName, t) => (
  fromName === toName
    ? t('roadTrip.roundTripFrom', { city: fromName })
    : `${fromName} ${t('roadTrip.to')} ${toName}`
);

/**
 * Titles for one day's road-trip card. A multi-day trip is labelled by that
 * day's own leg (Dublin to Universal, then Universal to Dublin) so the days
 * read as a sequence, while `tripTitle` still names the whole trip.
 *
 * @returns {{ dayTitle: string, tripTitle: string, segmentNumber: number, totalDays: number }}
 */
export const getRoadTripCardTitles = (roadTripData, t) => {
  const dayStops = roadTripData?.orderedStops || [];
  const allDays = roadTripData?.allDaysData || [];
  const totalDays = getRoadTripDayCount(roadTripData);

  const tripFirst = allDays[0]?.stops?.[0] || dayStops[0];
  const lastDayStops = allDays[allDays.length - 1]?.stops || [];
  const tripLast = lastDayStops[lastDayStops.length - 1] || dayStops[dayStops.length - 1];

  if (!tripFirst?.name || !tripLast?.name) {
    const fallback = t('roadTrip.title');
    return { dayTitle: fallback, tripTitle: fallback, segmentNumber: 1, totalDays };
  }

  const daysSuffix = totalDays > 1 ? ` (${totalDays} ${t('roadTrip.days')})` : '';
  const tripTitle = `${routeLabel(tripFirst.name, tripLast.name, t)}${daysSuffix}`;

  const dayFirst = dayStops[0];
  const dayLast = dayStops[dayStops.length - 1];
  const dayTitle = totalDays > 1 && dayFirst?.name && dayLast?.name
    ? routeLabel(dayFirst.name, dayLast.name, t)
    : tripTitle;

  return {
    dayTitle,
    tripTitle,
    segmentNumber: getRoadTripSegmentNumber(roadTripData),
    totalDays,
  };
};

export const buildRoadTripDragUpdates = ({
  roadTripCard,
  sourceDayKey,
  targetDayKey,
  tripStartDate,
}) => {
  if (!isDraggableRoadTripCard(roadTripCard) || !sourceDayKey || !targetDayKey) {
    return [];
  }

  if (sourceDayKey === targetDayKey) {
    return [];
  }

  const segmentItems = getRoadTripSegmentItems(roadTripCard);
  if (!segmentItems.length) {
    return [];
  }

  const tripStartKey = toDateKey(tripStartDate);
  const segmentDayIndex = roadTripCard._roadTripData?.dayIndex ?? 0;
  const newDayIndex = tripStartKey
    ? getDayIndexFromTripStart(tripStartKey, targetDayKey)
    : segmentDayIndex;

  return segmentItems.map((item) => {
    const td = parseTransportationData(item);
    let transportation_data;

    if (td?.road_trip_data) {
      const rtd = { ...td.road_trip_data, dayIndex: newDayIndex };
      if (Array.isArray(rtd.allDaysData)) {
        rtd.allDaysData = rtd.allDaysData.map((dayEntry, idx) => {
          const entryDayIndex = dayEntry.dayIndex ?? idx;
          if (entryDayIndex === segmentDayIndex) {
            return {
              ...dayEntry,
              date: targetDayKey,
              dayIndex: newDayIndex,
            };
          }
          return dayEntry;
        });
      }
      transportation_data = { ...td, road_trip_data: rtd };
    }

    const update = {
      id: item.id,
      start_datetime: setDateOnDateTime(item.start_datetime, targetDayKey),
    };

    if (item.end_datetime) {
      update.end_datetime = setDateOnDateTime(item.end_datetime, targetDayKey);
    }

    if (transportation_data) {
      update.transportation_data = transportation_data;
    }

    return update;
  });
};

export const findRoadTripCardById = (days, itemId) => {
  for (const day of days) {
    const match = day.items.find(
      (item) => item._isRoadTripCard && item.id === itemId
    );
    if (match) {
      return { card: match, sourceDayKey: getDayDateKey(day.date) };
    }
  }
  return null;
};

export const getRoadTripCardSourceDateKey = (roadTripCard) => {
  const firstItem = roadTripCard?._roadTripStops?.[0] || roadTripCard?._roadTripPois?.[0];
  if (!firstItem?.start_datetime) {
    return null;
  }
  return parseDateTimeString(firstItem.start_datetime).datePart;
};
